import type {
    ParsedLine,
    ParsedNumberField,
} from '@/pages/catalogImport/store';
import {
    REQUIRED_FIELD_KEYS,
    TEMPLATE_BODY_ROWS,
    TEMPLATE_FIELD_COLUMNS,
    TEMPLATE_LANGUAGES,
    TEMPLATE_SHEET_NAMES,
    TEMPLATE_VAT_VALUES,
    type TemplateCellFormat,
    type TemplateFieldKey,
} from '@/pages/catalogImport/template/catalogImportTemplateMeta';

/**
 * The parse rules of a catalog-import file, with no exceljs: how a header
 * finds its column, which rows count, and how a cell becomes a line's value.
 * `catalogImportParse.ts` is the exceljs adapter around them.
 *
 * The file's words come from the template meta (both languages), imported by
 * path rather than through the template barrel, so the worker (#183) does not
 * pull the template builder in with them.
 */

/** Data rows one import takes; more is refused before any row is mapped. */
export const PARSE_ROW_LIMIT = TEMPLATE_BODY_ROWS;

/** Rows between two progress reports, each one also a chance to cancel. */
export const PARSE_PROGRESS_EVERY = 200;

/** A cell's value after exceljs's wrappers (formula, rich text, link) are lifted. */
export type CellScalar = string | number | boolean | Date | null;

/** Headers, sheet names and VAT values compare trimmed, with one space between words, ignoring case. */
export const comparableText = (text: string): string =>
    text.trim().replace(/\s+/g, ' ').toLowerCase();

/** Whether a sheet is the Products sheet, in either language. */
export const isProductsSheetName = (name: string): boolean =>
    TEMPLATE_LANGUAGES.some(
        (language) =>
            comparableText(TEMPLATE_SHEET_NAMES.products[language]) ===
            comparableText(name),
    );

// ---- Headers ----------------------------------------------------------------

/** Where one column's cells go on the line. */
export type ColumnTarget =
    | { kind: 'field'; key: TemplateFieldKey; format: TemplateCellFormat }
    | { kind: 'attribute'; attributeTypeName: string };

/** One recognised column; `column` is 1-based, as Excel and exceljs count. */
export type MatchedColumn = { column: number; target: ColumnTarget };

export type HeaderMatch = {
    columns: MatchedColumn[];
    /** Headers that name nothing, as written; their columns are not read. */
    ignoredColumns: string[];
    /** Required fields with no column. */
    missing: TemplateFieldKey[];
};

/**
 * Every header in either language, normalized, to its column target. Built
 * once per parse: the attribute types are the caller's.
 */
const headerTargets = (
    attributeTypeNames: readonly string[],
): Map<string, ColumnTarget> => {
    const targets = new Map<string, ColumnTarget>();
    for (const column of TEMPLATE_FIELD_COLUMNS) {
        for (const language of TEMPLATE_LANGUAGES) {
            targets.set(comparableText(column.header[language]), {
                kind: 'field',
                key: column.key,
                format: column.format,
            });
        }
    }
    for (const name of attributeTypeNames) {
        const header = comparableText(name);
        // A fixed column wins over an attribute type of the same name.
        if (header !== '' && !targets.has(header)) {
            targets.set(header, { kind: 'attribute', attributeTypeName: name });
        }
    }
    return targets;
};

const targetId = (target: ColumnTarget): string =>
    target.kind === 'field'
        ? `field:${target.key}`
        : `attribute:${comparableText(target.attributeTypeName)}`;

/**
 * Matches a header row, given as texts by column (index 0 is column A).
 * Either language matches, and one file may mix them. A blank header is
 * skipped; an unknown one is listed as ignored. A column whose target an
 * earlier column already took is listed as ignored too, so a repeated header
 * never overwrites the first one's value.
 */
export const matchHeaders = (
    headers: readonly string[],
    attributeTypeNames: readonly string[],
): HeaderMatch => {
    const targets = headerTargets(attributeTypeNames);
    const taken = new Set<string>();
    const columns: MatchedColumn[] = [];
    const ignoredColumns: string[] = [];
    headers.forEach((header, index) => {
        const text = header.trim();
        if (text === '') {
            return;
        }
        const target = targets.get(comparableText(text));
        if (target === undefined || taken.has(targetId(target))) {
            ignoredColumns.push(text);
            return;
        }
        taken.add(targetId(target));
        columns.push({ column: index + 1, target });
    });
    const missing = REQUIRED_FIELD_KEYS.filter(
        (key) => !taken.has(`field:${key}`),
    );
    return { columns, ignoredColumns, missing };
};

// ---- Cells ------------------------------------------------------------------

const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * A date cell as `yyyy-MM-dd`: the calendar day nearest its moment in UTC.
 * Excel stores a date as a day count that exceljs turns into UTC midnight; a
 * file written from local midnight east of UTC lands hours before it, so
 * reading the plain UTC day would move such a date back by one.
 */
export const isoDateOf = (value: Date): string => {
    const shifted = new Date(value.getTime() + 12 * 60 * 60 * 1000);
    return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(
        shifted.getUTCDate(),
    )}`;
};

/** A cell as text, trimmed; `''` when blank. A date cell reads as its ISO day. */
export const cellText = (value: CellScalar): string => {
    if (value === null) return '';
    if (value instanceof Date) return isoDateOf(value);
    if (typeof value === 'string') return value.trim();
    return String(value);
};

/** Plain decimal notation, as a number cell shows when it was typed as text. */
const DECIMAL_TEXT = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;

/**
 * A number cell's value, as Excel holds it: never scaled, never rounded. A
 * text cell counts when it is plain decimal notation, spaces aside (`12.50`,
 * `1 200`); anything else, a comma included, is not a number (`null`),
 * because the API takes amounts only as JSON numbers. `lineOf` keeps such a
 * cell's text apart from a blank one (`unreadable`).
 */
export const cellNumber = (value: CellScalar): number | null => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string') {
        const compact = value.replace(/\s/g, '');
        return DECIMAL_TEXT.test(compact) ? Number(compact) : null;
    }
    return null;
};

const VAT_TYPE_BY_TEXT = new Map<string, 'standard' | 'exempt'>(
    (['standard', 'exempt'] as const).flatMap((vatType) => [
        [vatType, vatType],
        ...TEMPLATE_LANGUAGES.map(
            (language) =>
                [
                    comparableText(TEMPLATE_VAT_VALUES[vatType][language]),
                    vatType,
                ] as const,
        ),
    ]),
);

/**
 * The VAT column: either language's two values (or the wire values) map to
 * `standard` / `exempt`, and blank is `standard`. Other text goes as typed,
 * so the server names it in `VAT_TYPE_UNKNOWN` instead of the row quietly
 * becoming standard.
 */
export const vatTypeOf = (text: string): string =>
    text === ''
        ? 'standard'
        : (VAT_TYPE_BY_TEXT.get(comparableText(text)) ?? text);

/** The Unit column: blank is `Piece`; anything else goes as typed (`UNIT_UNKNOWN`). */
export const unitOf = (text: string): string => (text === '' ? 'Piece' : text);

// ---- Lines ------------------------------------------------------------------

type TextLineField =
    | 'sku'
    | 'barcode'
    | 'name'
    | 'categoryText'
    | 'brandText'
    | 'supplierText'
    | 'lotNumber';

type NumberLineField = ParsedNumberField;

type DateLineField = 'purchaseDate' | 'expiryDate' | 'saleDueDate';

/** The line field each fixed column fills; category, brand and supplier send their text. */
const TEXT_FIELDS: Partial<Record<TemplateFieldKey, TextLineField>> = {
    sku: 'sku',
    barcode: 'barcode',
    name: 'name',
    category: 'categoryText',
    brand: 'brandText',
    supplier: 'supplierText',
    lotNumber: 'lotNumber',
};

const NUMBER_FIELDS: Partial<Record<TemplateFieldKey, NumberLineField>> = {
    quantity: 'quantity',
    cost: 'cost',
    salePrice: 'salePrice',
    wholesalePrice: 'wholesalePrice',
    minimumQuantity: 'minimumQuantity',
};

const DATE_FIELDS: Partial<Record<TemplateFieldKey, DateLineField>> = {
    purchaseDate: 'purchaseDate',
    expiryDate: 'expiryDate',
    saleDueDate: 'saleDueDate',
};

/** Whether a row has anything in a recognised column; a row that does not is skipped. */
export const rowHasContent = (
    cells: readonly CellScalar[],
    columns: readonly MatchedColumn[],
): boolean =>
    columns.some(({ column }) => cellText(cells[column] ?? null) !== '');

/**
 * One row as the request line the server expects. `cells` is indexed by
 * 1-based column, as exceljs's `row.values`.
 *
 * - Texts are trimmed; a blank one is left off the line.
 * - Unit and VAT are always on the line: blank unit is `Piece`, blank VAT is
 *   `standard`.
 * - Numbers are the cell's value in GEL; a blank one is left off (the
 *   server then reports a required one as missing). An unreadable one
 *   (`12,5x`, `n/a`) is left off too, and its text goes in `unreadable`,
 *   which the app never sends: the store makes it a problem of the row.
 * - Dates are `yyyy-MM-dd` for a date cell, else the text as typed; a
 *   number in a date column is not a date cell and goes as its digits.
 * - Attribute columns become `optionValues` by the type's name; a blank cell
 *   adds no axis, which the server reads as not given.
 */
export const lineOf = (
    rowNumber: number,
    cells: readonly CellScalar[],
    columns: readonly MatchedColumn[],
): ParsedLine => {
    const line: ParsedLine = { rowNumber };
    let unit = '';
    let vatType = '';
    const optionValues: NonNullable<ParsedLine['optionValues']> = [];
    for (const { column, target } of columns) {
        const value = cells[column] ?? null;
        if (target.kind === 'attribute') {
            const valueText = cellText(value);
            if (valueText !== '') {
                optionValues.push({
                    attributeTypeName: target.attributeTypeName,
                    valueText,
                });
            }
            continue;
        }
        const textField = TEXT_FIELDS[target.key];
        const numberField = NUMBER_FIELDS[target.key];
        const dateField = DATE_FIELDS[target.key];
        if (target.key === 'unit') {
            unit = cellText(value);
        } else if (target.key === 'vatType') {
            vatType = cellText(value);
        } else if (textField !== undefined) {
            const text = cellText(value);
            if (text !== '') line[textField] = text;
        } else if (numberField !== undefined) {
            const number = cellNumber(value);
            const text = cellText(value);
            if (number !== null) {
                line[numberField] = number;
            } else if (text !== '') {
                // Nothing valid to send, but not blank either: kept for the
                // owner to correct rather than imported as if it were.
                line.unreadable = { ...line.unreadable, [numberField]: text };
            }
        } else if (dateField !== undefined) {
            // A date cell is its ISO day; other text goes as typed, and the
            // server reports what it cannot read as `DATE_INVALID`.
            const date = cellText(value);
            if (date !== '') line[dateField] = date;
        }
    }
    line.unit = unitOf(unit);
    line.vatType = vatTypeOf(vatType);
    if (optionValues.length > 0) {
        line.optionValues = optionValues;
    }
    return line;
};
